import { FC, useContext, useEffect, useMemo, useState } from "react"
import { FlyoutFooter } from "@app/shared/Flyouts";
import { SortDirections, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { Link } from "react-router-dom";
import { SharedEcdDrillInTableData, sharedEcdDrillInColumnConfig, sharedEcdDrillInCustomSort, sharedEcdDrillInPaginationProps, sharedEcdDrillInSlicedTableData } from "./EcdDrillInConfig";
import { SearchBox } from "@app/shared/SearchBox";

export interface EcdCompositeFillableGapsTableData extends SharedEcdDrillInTableData {
  fillableBySingleProvider: string;
}

const EcdCompositeFillableGapsFlyout: FC = () => {
  const ecdContext = useContext(EnterpriseComplianceDashboardContext);
  const [data, setData] = useState<EcdCompositeFillableGapsTableData[]>([]);
  const [filterString, setFilterString] = useState("");
  const [skip, setSkip] = useState(0);
  const take = 100;

  useEffect(() => {
    ecdContext.getCompositeFillableGapsDrillInData().then((response) => {
      const newData: EcdCompositeFillableGapsTableData[] = response.map((row) => {
        return {
          /* oxlint-disable sort-keys */
          [wafflesKeyColumnName]: row.customProjectId.toString(),
          projectName: <Link key={row.projectName} to={`/projects/${row.customProjectId}/adequacy?viewBy=ALL&all=false&gaps=true`} target="_blank">{row.projectName}</Link>,
          compositeFillableGaps: row.fillableGapCount.toLocaleString(),
          fillableBySingleProvider: row.fillableByOneGapCount.toLocaleString(),
          unfillableGaps: row.unfillableGapCount.toLocaleString(),
          overallGaps: row.gapCount.toLocaleString(),
          /* oxlint-enable sort-keys */
        }
      });
      customSort(newData, "compositeFillableGaps", SortDirections.Desc);
    })
  }, []);

  const customSort = (records: EcdCompositeFillableGapsTableData[], column: keyof EcdCompositeFillableGapsTableData, direction: SortDirections) => {
    const newData = sharedEcdDrillInCustomSort<EcdCompositeFillableGapsTableData>(
      data.length > 0 ? data : records,
      column,
      direction,
      "compositeFillableGaps",
      SortDirections.Desc
    );

    setData(newData);
    // records is undefined because of passThroughSorting,
    // but we can't just leave it unused either
    return records;
  }

  const paginationProps: WafflesPaginationProps = useMemo(() => (
    sharedEcdDrillInPaginationProps(skip, take, setSkip, data, filterString)
  ), [data, skip, take, filterString]);

  const slicedData = useMemo(() => {
    return sharedEcdDrillInSlicedTableData<EcdCompositeFillableGapsTableData>(data, filterString, skip, take);
  }, [data, skip, take, filterString]);

  return (
    <>
      <SearchBox
        fieldsetClass="row mx-0"
        inputLabel="Filter on Project Name"
        onSubmit={(value) => setFilterString(value)}
        testId={`ecd-composite-fillable-gaps-flyout__filter`}
      />
      <Waffles<EcdCompositeFillableGapsTableData>
        columns={sharedEcdDrillInColumnConfig}
        customSort={customSort}
        data={slicedData}
        enablePagination={true}
        fullWidth={true}
        loading={data.length === 0 && filterString === ""}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId="ecd-composite-fillable-gaps-flyout__table"
      />
      <FlyoutFooter
        submitButton={null}
      />
    </>
  );
}

export default EcdCompositeFillableGapsFlyout;
